import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

import { Checkbox } from '@/components/form/inputs/checkbox';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/ui/dialog';
import { UserRole, UserRoleSchema } from '@/entities/user-role';

import { CreateRoleMutation } from '../mutations/create-role';
import { GetSitesQuery } from '../queries/get-sites';

const FormSchema = z.object({
  name: z.string().min(1, 'Required'),
  permission: UserRoleSchema,
  siteIds: z.array(z.string()).min(1, 'Required').nullable(),
});

type FormInputs = z.infer<typeof FormSchema>;

export type CreateRoleDialogProps = {
  open: boolean;
  onClose: () => void;
};

const permissions = Object.values(UserRole);

enum SiteIdsMode {
  ALL = 'all',
  SELECTED = 'selected',
}

export function CreateRoleDialog({ open, onClose }: CreateRoleDialogProps) {
  const sitesQuery = GetSitesQuery.useQuery();
  const createRole = CreateRoleMutation.useMutation();

  const form = useForm<FormInputs>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: '',
      permission: UserRole.CUSTOMER,
      siteIds: null,
    },
  });

  function handleClose() {
    onClose();
  }

  async function handleSubmit(data: FormInputs) {
    await toast.promise(createRole.mutateAsync(data), {
      pending: 'Creating role...',
      success: 'Role created successfully!',
      error: 'Failed to create role. Please try again.',
    });
    handleClose();
    form.reset();
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <DialogTitle>Create Role</DialogTitle>
          <DialogBody className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="permission"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Permission</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select permission" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {permissions.map((permission) => (
                            <SelectItem key={permission} value={permission}>
                              {permission}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="siteIds"
              render={({ field }) => {
                const mode = Array.isArray(field.value)
                  ? SiteIdsMode.SELECTED
                  : SiteIdsMode.ALL;

                const selectedSiteIds = Array.isArray(field.value)
                  ? Object.fromEntries(field.value.map((id) => [id, true]))
                  : {};

                return (
                  <FormItem>
                    <FormLabel>Sites</FormLabel>
                    <FormControl>
                      <>
                        <Select
                          value={mode}
                          onValueChange={(mode) => {
                            if (mode === SiteIdsMode.ALL) {
                              field.onChange(null);
                            } else if (mode === SiteIdsMode.SELECTED) {
                              field.onChange([]);
                            }
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectItem value={SiteIdsMode.ALL}>
                                All sites
                              </SelectItem>
                              <SelectItem value={SiteIdsMode.SELECTED}>
                                Selected Sites
                              </SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                        {mode === SiteIdsMode.SELECTED ? (
                          <div className="mt-4 space-y-2">
                            {(sitesQuery.data?.sites ?? []).map((site) => (
                              <div
                                key={site.id}
                                className="flex items-center space-x-3"
                              >
                                <Checkbox
                                  id={`site-${site.id}`}
                                  checked={selectedSiteIds[site.id] ?? false}
                                  onChange={(e) => {
                                    const currentIds = Array.isArray(
                                      field.value,
                                    )
                                      ? field.value
                                      : [];
                                    if (e.target.checked) {
                                      field.onChange([...currentIds, site.id]);
                                    } else {
                                      field.onChange(
                                        currentIds.filter(
                                          (id) => id !== site.id,
                                        ),
                                      );
                                    }
                                  }}
                                />
                                <label
                                  htmlFor={`site-${site.id}`}
                                  className="cursor-pointer text-sm font-medium text-gray-700"
                                >
                                  {site.fullFrontendId}
                                </label>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </DialogBody>
          <DialogActions>
            <Button variant="ghost" type="button" onClick={handleClose}>
              cancel
            </Button>
            <Button
              className="bg-primary-500 hover:bg-primary-400"
              type="submit"
              disabled={createRole.isLoading}
            >
              {createRole.isLoading ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Creating
                </>
              ) : (
                'Create'
              )}
            </Button>
          </DialogActions>
        </form>
      </Form>
    </Dialog>
  );
}
